import { useState } from "react";
import { Link } from "react-router";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { useUpdateName } from "@/lib/admin";
import type { Me } from "@/lib/me";

export function Profile({ me }: { me: Me }) {
  const [name, setName] = useState(me.name ?? "");
  const update = useUpdateName();

  return (
    <main className="mx-auto max-w-lg px-5 py-7">
      <Link
        to="/"
        className="text-muted-foreground hover:text-foreground mb-4 inline-flex items-center gap-1.5 text-sm"
      >
        <ArrowLeft size={14} /> Back to apps
      </Link>

      <h1 className="text-xl font-semibold tracking-tight">Your profile</h1>
      <p className="text-muted-foreground mt-1 mb-6 text-sm">
        Your name is how colleagues see you across the apps.
      </p>

      <form
        className="border-border bg-card space-y-4 rounded-lg border p-4"
        onSubmit={(e) => {
          e.preventDefault();
          update.mutate(name);
        }}
      >
        <div>
          <label htmlFor="name" className="mb-1.5 block text-sm font-medium">
            Name
          </label>
          <Input
            id="name"
            value={name}
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your full name"
          />
        </div>

        <div>
          <label htmlFor="email" className="mb-1.5 block text-sm font-medium">
            Email
          </label>
          {/*
            Email is identity — it's what Layer 1 matches on and what Clerk
            verifies. Changing it here would silently detach the person from
            their access, so it's read-only by design.
          */}
          <Input id="email" value={me.email} readOnly disabled />
          <p className="text-muted-foreground mt-1.5 text-xs">
            Your email is your sign-in identity and cannot be changed here.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button type="submit" size="sm" disabled={update.isPending}>
            {update.isPending ? "Saving…" : "Save"}
          </Button>
          {update.isSuccess && !update.data?.warning && (
            <span className="text-success text-sm">Saved</span>
          )}
        </div>

        {update.data?.warning && (
          <p className="text-warning text-sm">{update.data.warning}</p>
        )}
        {update.error && (
          <p className="text-destructive text-sm">
            {update.error instanceof Error ? update.error.message : "Could not save."}
          </p>
        )}
      </form>
    </main>
  );
}
